// Hero — Stage Picker (v2 with masthead image + creative copy + LearnixTa quote).
// Two-column layout on desktop: text left, photo collage right.
// Same single-question UX, but now with personality and visual weight.

import React from 'react';
import { HeroOpening } from './hero/HeroOpening';
import HeroStageCard, { HERO_STAGE_CARD_CSS } from './HeroStageCard';
import { HERO_CARD_COVERS } from '../data/heroCardCovers';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  GraduationCap,
  Compass,
  Rocket,
  Briefcase,
  Sprout,
  Sparkles,
  LayoutDashboard,
  Quote,
  Cpu,
  Landmark,
  Wrench,
  Trophy,
  ShieldAlert,
  HeartHandshake,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useT } from '../i18n';
import { useRoleCount } from '../hooks/useRoleCount';
import useCanSeeCounts from '../hooks/useCanSeeCounts';

const HERO_TALL_IMG = '/images/hero-tall.webp';
const HERO_WIDE_IMG = '/images/hero-wide.webp';

const ManifestoBody = ({ compact = false }) => {
  const body = compact
    ? 'text-[12.5px] leading-relaxed text-gray-200 mb-2.5'
    : 'text-sm md:text-[15px] leading-relaxed text-gray-200 mb-3';
  return (
  <>
    <p className={body}>
      Explore <b className="text-white">blue-collar careers, entrepreneurship and agriculture</b>&mdash;from skilled trades to building businesses and growing food.
    </p>
    <p className={body}>
      As times change, keep learning and build skills that help you earn and grow.
    </p>
    <p className={compact ? 'text-sm font-bold text-teal-300' : 'text-base md:text-lg font-bold text-teal-300'}>
      Understand the work, income and challenges&mdash;take your family&rsquo;s guidance and choose the path that suits you.
    </p>
  </>
  );
};

export const HERO_STAGES = [
    {
      key: 'career-clarity',
      who: 'After Class 12 / Intermediate • 18+',
      label: 'After Class 12 / Intermediate • 18+',
      headline: 'Career Clarity',
      sub: 'Not sure what to study? Take a short test and get a clear direction.',
      to: '/career-clarity',
      Icon: Sprout,
      border: 'border-amber-200 hover:border-amber-400',
      iconBg: 'bg-amber-500',
      chip: 'text-amber-700 bg-amber-100',
      shadow: 'hover:shadow-amber-200/60',
    },
    {
      key: 'specializations',
      who: 'B.Tech · BBA · MBA — 1st / 2nd year',
      label: 'After 12th · 1st / 2nd year',
      headline: 'Inner Specializations',
      sub: 'After 12th or in 1st/2nd year? See what each B.Tech, BBA or MBA specialization really involves before you commit.',
      to: '/specializations',
      Icon: Compass,
      border: 'border-teal-200 hover:border-teal-500',
      iconBg: 'bg-teal-600',
      chip: 'text-teal-700 bg-teal-100',
      shadow: 'hover:shadow-teal-200/60',
    },
    {
      key: 'job-roles',
      who: 'Final year · Freshers · Working',
      label: 'Final year · Fresher · Working',
      headline: 'Future-Ready Job Roles',
      sub: 'Final year, fresher or working? Find roles that fit you — with the AI reality.',
      to: '/job-roles',
      Icon: GraduationCap,
      border: 'border-blue-200 hover:border-blue-500',
      iconBg: 'bg-blue-600',
      chip: 'text-blue-700 bg-blue-100',
      shadow: 'hover:shadow-blue-200/60',
    },
    {
      key: 'ai-pathway',
      who: 'Anyone curious about AI careers',
      label: 'New AI-native careers',
      headline: 'Future AI Agent Pathway',
      sub: 'Explore the new AI careers — and how people work alongside AI agents.',
      to: '/ai-pathway',
      Icon: Cpu,
      border: 'border-indigo-200 hover:border-indigo-500',
      iconBg: 'bg-indigo-600',
      chip: 'text-indigo-700 bg-indigo-100',
      shadow: 'hover:shadow-indigo-200/60',
    },
    {
      key: 'ai-risk-check',
      who: 'Working professionals · 30 sec',
      label: '30-second check',
      headline: 'Is AI Coming for Your Role?',
      sub: 'See the real AI-disruption risk on your role — what stays human and the exact skills to add now.',
      to: '/ai-risk-check',
      Icon: ShieldAlert,
      border: 'border-sky-200 hover:border-sky-500',
      iconBg: 'bg-sky-600',
      chip: 'text-sky-700 bg-sky-100',
      shadow: 'hover:shadow-sky-200/60',
    },
    {
      key: 'build-badges',
      who: 'Students who want proof of skill',
      label: 'Proof of skill',
      headline: 'Build Badges',
      sub: 'Ship one real project per role, paste your GitHub link, earn a verified badge for LinkedIn.',
      to: '/build-badges',
      Icon: Trophy,
      border: 'border-yellow-200 hover:border-yellow-500',
      iconBg: 'bg-yellow-500',
      chip: 'text-yellow-800 bg-yellow-100',
      shadow: 'hover:shadow-yellow-200/60',
    },
    {
      key: 'internships',
      who: 'College students · Official portals',
      label: 'Official portals · Student internships',
      headline: 'Internship Pathfinder',
      sub: 'Explore government and company internships, check your eligibility and continue safely to the official application portal.',
      to: '/pathways/internships',
      Icon: Briefcase,
      border: 'border-cyan-200 hover:border-cyan-500',
      iconBg: 'bg-cyan-700',
      chip: 'text-cyan-800 bg-cyan-100',
      shadow: 'hover:shadow-cyan-200/60',
      aria: 'Explore internship pathways',
    },
    {
      key: 'fresher-launchpad',
      who: 'Graduates hunting the first job',
      label: 'First job · Verified official routes',
      headline: 'Fresher Launchpad',
      sub: 'Build the skills, confidence and official job-search plan to land your first role. 100 verified official resources.',
      to: '/pathways/fresher-launchpad',
      Icon: Rocket,
      border: 'border-emerald-200 hover:border-emerald-500',
      iconBg: 'bg-emerald-700',
      chip: 'text-emerald-800 bg-emerald-100',
      shadow: 'hover:shadow-emerald-200/60',
      aria: 'Explore the Fresher Launchpad',
    },
    {
      key: 'career-switch-growth',
      who: 'Switchers · Returners · Growth',
      label: 'Next move · Verified official routes',
      headline: 'Career Switch & Growth',
      sub: 'Switch careers, return after a break or grow through verified official destinations. 199 verified official resources.',
      to: '/pathways/career-switch-growth',
      Icon: TrendingUp,
      border: 'border-violet-200 hover:border-violet-500',
      iconBg: 'bg-violet-700',
      chip: 'text-violet-800 bg-violet-100',
      shadow: 'hover:shadow-violet-200/60',
      aria: 'Explore Career Switch and Growth',
    },
    {
      key: 'banking',
      who: 'Graduates aiming at SBI · IBPS · RBI',
      label: 'SBI · IBPS · RBI',
      headline: 'Banking Careers',
      sub: 'Bank and banking-exam careers — real exam paths and what to expect.',
      to: '/banking',
      Icon: Landmark,
      border: 'border-emerald-200 hover:border-emerald-500',
      iconBg: 'bg-emerald-600',
      chip: 'text-emerald-700 bg-emerald-100',
      shadow: 'hover:shadow-emerald-200/60',
    },
    {
      key: 'blue-collar',
      who: '10th / ITI / Diploma · Skilled trades',
      label: 'Skilled trades that pay',
      headline: 'Blue-Collar Jobs',
      sub: 'Skilled work that pays — beauty, repairs, driving, delivery and more.',
      to: '/blue-collar',
      Icon: Wrench,
      border: 'border-orange-200 hover:border-orange-500',
      iconBg: 'bg-orange-500',
      chip: 'text-orange-700 bg-orange-100',
      shadow: 'hover:shadow-orange-200/60',
    },
    {
      key: 'ownpath',
      who: 'Aspiring founders · Any budget',
      label: 'All over India · Govt schemes',
      headline: 'Entrepreneurship · OwnPath',
      sub: 'Want to start your own? Get matched to business ideas and government schemes.',
      to: '/ownpath',
      Icon: Rocket,
      border: 'border-rose-200 hover:border-rose-500',
      iconBg: 'bg-rose-600',
      chip: 'text-rose-700 bg-rose-100',
      shadow: 'hover:shadow-rose-200/60',
    },
    {
      key: 'agriculture',
      who: 'Farming families · Agri-business',
      label: 'Farming · Agri-business · Rural enterprise',
      headline: 'Agriculture',
      sub: 'Explore farming, hydroponics, farm services and rural enterprise. Learn before you invest.',
      to: '/agriculture',
      Icon: Sprout,
      border: 'border-green-200 hover:border-green-600',
      iconBg: 'bg-green-700',
      chip: 'text-green-800 bg-green-100',
      shadow: 'hover:shadow-green-200/60',
      aria: 'Explore the Agriculture pathway',
    },
    {
      key: 'life-skills',
      who: 'Everyone · Free',
      label: 'For everyone · Free',
      headline: 'Life Skills — For Every Future',
      sub: 'Practical skills to earn, adapt and grow — with honest income, risks and dignity. Free for everyone, always.',
      to: '/life-skills',
      Icon: HeartHandshake,
      border: 'border-lime-200 hover:border-lime-500',
      iconBg: 'bg-lime-600',
      chip: 'text-lime-800 bg-lime-100',
      shadow: 'hover:shadow-lime-200/60',
    },
    {
      key: 'the-ai-shift',
      who: 'Students · Parents · Educators',
      label: 'AI • AGI • ASI · Prepare Now',
      headline: 'The AI Shift',
      sub: 'Understand the forecasts. Build practical options. A 2026–2032 preparation plan with state evidence and sources.',
      to: '/the-ai-shift',
      Icon: GraduationCap,
      border: 'border-teal-200 hover:border-teal-500',
      iconBg: 'bg-teal-700',
      chip: 'text-teal-800 bg-teal-100',
      shadow: 'hover:shadow-teal-200/60',
    },
  ];

const Hero = () => {
  const { user } = useAuth();
  const { t } = useT();
  const firstName = user?.name?.split(' ')?.[0];
  const roleCount = useRoleCount();
  const canSeeCounts = useCanSeeCounts();
  const rc = roleCount ?? 260;


  return (
    <section className="relative pt-28 md:pt-32 pb-16 px-4 md:px-6 overflow-hidden bg-white">
      {/* Subtle blob backdrop */}
      <div aria-hidden className="absolute inset-0 -z-10">
        <div className="absolute top-0 -left-20 w-[420px] h-[420px] bg-teal-100 rounded-full filter blur-3xl opacity-60" />
        <div className="absolute top-1/3 -right-32 w-[380px] h-[380px] bg-amber-100 rounded-full filter blur-3xl opacity-50" />
        <div className="absolute bottom-0 left-1/3 w-[320px] h-[320px] bg-rose-100 rounded-full filter blur-3xl opacity-40" />
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-2 text-xs font-semibold tracking-widest text-teal-700 uppercase mb-5" data-testid="hero-brand-pill">
          <Sparkles size={14} className="text-teal-600" />
          {t('hero.brand_pill')}
        </div>

        {/* Opening message only; existing typography and the rest of the hero stay intact. */}
        <HeroOpening />

        {/* Text, then image below */}
        <div>
          <div className="min-w-0">

            {/* MOBILE — portrait image, shown whole (no crop) */}
            <div className="lg:hidden mt-8" data-testid="hero-mobile-masthead">
              <div className="relative max-w-[340px] mx-auto" data-testid="hero-mobile-image-card">
                <div className="absolute -inset-2.5 bg-gradient-to-br from-teal-500/15 via-amber-200/30 to-rose-200/30 rounded-[32px] -rotate-2" aria-hidden />
                <img
                  src={HERO_TALL_IMG}
                  alt="Agriculture, skilled trades and entrepreneurship — careers beyond digital jobs"
                  className="relative w-full h-auto rounded-[28px] shadow-xl block"
                  style={{ aspectRatio: '941 / 1672' }}
                  loading="eager"
                  data-testid="hero-mobile-image"
                />
                <div className="lxt-hero-vignette rounded-[28px]" aria-hidden data-testid="hero-mobile-vignette" />
              </div>

              {/* Your future goes beyond digital jobs — always visible on mobile. */}
              <div
                className="mt-6 max-w-[360px] mx-auto bg-gray-900 rounded-3xl px-5 pt-6 pb-5 text-white shadow-xl"
                data-testid="hero-manifesto-panel"
              >
                <div className="flex items-center gap-2 mb-2.5">
                  <Sparkles size={15} className="text-amber-300" />
                  <h3 className="text-lg font-black tracking-tight">Your future goes beyond digital jobs.</h3>
                </div>
                <ManifestoBody compact />
              </div>
            </div>
          </div>

          {/* DESKTOP — wide image, full container width, shown whole (no crop) */}
          <div className="hidden lg:block relative mt-10" data-testid="hero-masthead">
            <div className="relative group/mast" data-testid="hero-image-card">
              {/* Background frame */}
              <div className="absolute -inset-3 bg-gradient-to-br from-teal-500/15 via-amber-200/30 to-rose-200/30 rounded-[40px] -rotate-1 transition-all duration-700 group-hover/mast:from-teal-500/30 group-hover/mast:via-amber-300/40 group-hover/mast:to-rose-300/40 group-hover/mast:rotate-0" aria-hidden />

              <div className="relative rounded-[36px] overflow-hidden shadow-2xl bg-gray-200" style={{ aspectRatio: '1672 / 941' }}>
                <img
                  src={HERO_WIDE_IMG}
                  alt="Agriculture, skilled trades and entrepreneurship — careers beyond digital jobs"
                  className="absolute inset-0 w-full h-full object-fill"
                  loading="eager"
                  data-testid="hero-desktop-image"
                />
                <div className="lxt-hero-vignette" aria-hidden data-testid="hero-desktop-vignette" />

                {/* Sparkles — twinkle in on hover */}
                <div className="absolute inset-0 pointer-events-none opacity-0 group-hover/mast:opacity-100 transition-opacity duration-500" aria-hidden data-testid="hero-masthead-sparkles">
                  {[
                    { top: '8%',  left: '12%', size: 18, color: '#5eead4', delay: '0s' },
                    { top: '15%', left: '78%', size: 24, color: '#fcd34d', delay: '0.25s' },
                    { top: '30%', left: '55%', size: 12, color: '#ffffff', delay: '0.5s' },
                    { top: '42%', left: '18%', size: 20, color: '#fcd34d', delay: '0.15s' },
                    { top: '55%', left: '85%', size: 14, color: '#5eead4', delay: '0.65s' },
                    { top: '68%', left: '38%', size: 22, color: '#ffffff', delay: '0.4s' },
                    { top: '78%', left: '68%', size: 16, color: '#fcd34d', delay: '0.8s' },
                    { top: '88%', left: '22%', size: 12, color: '#5eead4', delay: '0.3s' },
                    { top: '22%', left: '35%', size: 10, color: '#ffffff', delay: '0.95s' },
                    { top: '60%', left: '8%',  size: 14, color: '#fcd34d', delay: '0.55s' },
                  ].map((s, i) => (
                    <svg
                      key={i}
                      viewBox="0 0 24 24"
                      className="hero-sparkle absolute"
                      style={{ top: s.top, left: s.left, width: s.size, height: s.size, animationDelay: s.delay, filter: `drop-shadow(0 0 ${s.size / 3}px ${s.color})` }}
                      fill={s.color}
                    >
                      <path d="M12 0 L14.6 9.4 L24 12 L14.6 14.6 L12 24 L9.4 14.6 L0 12 L9.4 9.4 Z" />
                    </svg>
                  ))}
                </div>

                {/* Manifesto overlay — glass card reveals on hover */}
                <div
                  className="absolute inset-x-0 bottom-0 opacity-0 translate-y-6 group-hover/mast:opacity-100 group-hover/mast:translate-y-0 transition-all duration-700 delay-200 ease-out"
                  data-testid="hero-manifesto-overlay"
                >
                  <div className="w-full bg-gradient-to-t from-black/90 via-black/80 to-black/55 backdrop-blur-[3px] px-8 pt-8 pb-6 text-white">
                    <div className="flex items-center gap-2 mb-2.5">
                      <Sparkles size={15} className="text-amber-300" />
                      <h3 className="text-lg font-black tracking-tight">Your future goes beyond digital jobs.</h3>
                    </div>
                    <div className="max-w-3xl"><ManifestoBody /></div>
                  </div>
                </div>
              </div>

              {/* Sparkle twinkle keyframes */}
              <style>{`
                @keyframes heroSparkleTwinkle {
                  0%, 100% { transform: scale(0.4) rotate(0deg);  opacity: 0.25; }
                  50%      { transform: scale(1.15) rotate(90deg); opacity: 1; }
                }
                .hero-sparkle {
                  animation: heroSparkleTwinkle 1.6s ease-in-out infinite;
                  transform-origin: center;
                }
                @media (prefers-reduced-motion: reduce) {
                  .hero-sparkle { animation: none; opacity: 0.8; transform: none; }
                }
              `}</style>
            </div>

            {/* Below-image dock — stat pill left, quote right */}
            <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
              <div
                className="inline-flex items-center gap-2 bg-white border border-gray-100 rounded-full pl-4 pr-4 py-2 shadow-md"
                data-testid="hero-stat-card"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                <span className="text-xs font-black text-gray-900 whitespace-nowrap">Across Telangana &amp; AP</span>
                <span className="text-gray-300">·</span>
                <span className="text-[11px] text-emerald-600 font-semibold whitespace-nowrap">Clarity, not catalogue</span>
              </div>
              <div
                className="flex items-start gap-3 bg-white rounded-2xl shadow-md border border-gray-100 px-4 py-3"
                data-testid="hero-quote-card"
              >
                <Quote size={16} className="text-teal-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900 leading-snug">
                    &ldquo;We don&rsquo;t sell careers. We help you find yours.&rdquo;
                  </p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                    <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                      LearnixTa Manifesto
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Path cards — same UI, now 7 cards matching signup */}
        <div
          className="mt-14 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 hero-stage-grid"
          data-testid="hero-stage-grid"
        >
          {HERO_STAGES.map((s, idx) => (
            <HeroStageCard key={s.key} s={s} idx={idx} cover={HERO_CARD_COVERS.find((c) => c.to === s.to)} />
          ))}
        </div>

        {/* Card reveal animation — respects prefers-reduced-motion */}
        <style>{`
          @keyframes heroStageReveal {
            0%   { opacity: 0; transform: translateY(16px) scale(0.985); }
            100% { opacity: 1; transform: translateY(0)   scale(1);     }
          }
          .hero-stage-card {
            opacity: 0;
            animation: heroStageReveal 0.6s cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
          }
          @media (prefers-reduced-motion: reduce) {
            .hero-stage-card {
              opacity: 1;
              animation: none;
              transition: none;
            }
            .hero-stage-card:hover { transform: none !important; }
          }
          ${HERO_STAGE_CARD_CSS}
        `}</style>

        {/* Trust strip — admin sees numbers, users see qualitative magnitude */}
        <div
          className="mt-10 pt-8 border-t border-gray-200 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-600"
          data-testid="hero-trust-strip"
        >
          {(canSeeCounts
            ? [
                ['885+', 'honest previews'],
                ['260', 'future-ready roles'],
                ['', 'Built for Indian students'],
                ['', 'Free to start'],
              ]
            : [
                ['', 'An honest, growing career library'],
                ['', 'Future-ready roles · all paths'],
                ['', 'Built for Indian students'],
                ['', 'Free to start'],
              ]
          ).map(([num, label], i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
              {num && <span className="font-bold text-gray-900">{num}</span>}
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
